/**
 * ChatInput — multi-line text input with send button. Disabled while history
 * is loading. While a stream is in flight, the send button swaps in place to
 * a Stop button that cancels the stream.
 *
 * Enter sends, Shift+Enter inserts a newline. Auto-grows up to a hard cap.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Send, Square } from 'react-feather';

import { useMobile } from '../../../hooks/useMobile';
import { useChatStore } from '../../../stores/chatStore';

interface Props {
  disabled?: boolean;
  onSend: (text: string) => void;
  initialValue?: string;
  /** When true, the send button becomes a Stop button bound to onStop. */
  isStreaming?: boolean;
  /** Called when the user clicks Stop while streaming. */
  onStop?: () => void;
  /** Most recent user-sent message. Pressing ↑ in an empty input recalls
   *  this text for editing — same affordance as ChatGPT / Claude.ai. */
  lastUserMessage?: string | null;
}

export function ChatInput({
  disabled,
  onSend,
  initialValue,
  isStreaming,
  onStop,
  lastUserMessage,
}: Props) {
  const [value, setValue] = useState(initialValue ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useMobile();
  const setChatInputFocused = useChatStore((s) => s.setChatInputFocused);
  const inputDraft = useChatStore((s) => s.inputDraft);
  const setInputDraft = useChatStore((s) => s.setInputDraft);

  // Pill-to-input prefill: a component-detail card's follow-up pill writes
  // inputDraft. Pull it into the textarea, focus, and clear the draft. Does
  // NOT auto-send — the user confirms with Enter.
  useEffect(() => {
    if (inputDraft) {
      // Append to any in-progress text rather than clobbering it.
      setValue((v) => (v.trim() ? `${v} ${inputDraft}` : inputDraft));
      setInputDraft(null);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [inputDraft, setInputDraft]);

  // Refocus when a stream completes — the textarea is disabled while
  // streaming, which drops focus; refocus on re-enable so the user can
  // type the next question immediately. Desktop only: on mobile an
  // auto-focus pops the soft keyboard over the answer being read.
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && !isMobile && !disabled) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    wasStreamingRef.current = Boolean(isStreaming);
  }, [isStreaming, isMobile, disabled]);

  // Auto-grow the textarea up to maxHeight (144px ≈ 6 lines). Re-measure
  // on every value change: reset to 'auto' so scrollHeight reflects the
  // intrinsic content height, then snap height to scrollHeight. The
  // textarea's own `overflow-y-auto` + `style.maxHeight` cap the growth
  // and surface an internal scrollbar once content exceeds the cap.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) {return;}
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  const submit = () => {
    const text = value.trim();
    if (!text || disabled || isStreaming) {return;}
    onSend(text);
    setValue('');
    // Reset height on the next frame so the new empty value's layout
    // effect has already fired before we clear the inline height.
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    });
  };

  const handleButtonClick = () => {
    if (isStreaming) {
      onStop?.();
      return;
    }
    submit();
  };

  // Stop is always clickable while streaming. Send disables on empty/blocked.
  const buttonDisabled = isStreaming ? false : (disabled || !value.trim());

  return (
    <div className="border-t border-border bg-surface-card p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
      {/* Input row — textarea + Send/Stop button live inside the same
          bordered container; focus ring is applied via focus-within on
          the wrapper, not the textarea itself. */}
      <div className="flex items-center border border-border bg-surface-main transition-colors focus-within:border-orange">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          // Publish focus state to chatStore so Header can force-hide
          // on mobile while the user is in type mode. Desktop ignores
          // this signal (gated inside Header via useMobile()).
          onFocus={() => setChatInputFocused(true)}
          onBlur={() => setChatInputFocused(false)}
          onKeyDown={(e) => {
            // Desktop: Enter = send, Shift+Enter = newline (industry convention).
            // Mobile: Enter = newline (matches ChatGPT / Claude / iMessage /
            // WhatsApp / Discord); the visible Send button is the only
            // submit path. Multi-line composition on mobile without a Shift
            // key would otherwise be impossible.
            if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
              e.preventDefault();
              submit();
              return;
            }
            // ↑ recall: empty input pulls the last user message in for editing.
            // If the input has any content, fall through to the textarea's
            // native cursor-up behavior.
            if (e.key === 'ArrowUp' && value === '' && lastUserMessage) {
              e.preventDefault();
              setValue(lastUserMessage);
            }
          }}
          disabled={disabled || isStreaming}
          placeholder="Ask about your deal..."
          rows={1}
          // `enterKeyHint="enter"` tells mobile soft keyboards to render
          // a newline-themed Return key (matching the actual semantics:
          // Enter = newline on mobile, per the onKeyDown handler above).
          enterKeyHint="enter"
          style={{ maxHeight: '144px' }}
          className="custom-scrollbar block flex-1 resize-none overflow-y-auto bg-transparent px-3 py-2 text-base md:text-body text-txt-primary placeholder:text-txt-tertiary focus:outline-none disabled:opacity-50"
        />
        <button
          type="button"
          onClick={handleButtonClick}
          disabled={buttonDisabled}
          aria-label={isStreaming ? 'Stop' : 'Send'}
          className="m-1.5 inline-flex flex-shrink-0 items-center justify-center bg-transparent p-2 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:h-9 md:w-9 text-orange transition-opacity hover:opacity-80 disabled:opacity-50"
        >
          {isStreaming ? (
            <Square size={18} fill="currentColor" />
          ) : (
            <Send size={18} />
          )}
        </button>
      </div>
      {/* Keyboard hint — desktop only. */}
      <p className="mt-2 hidden text-caption text-txt-tertiary md:block">
        Enter to send · Shift+Enter for newline · ↑ to edit last
      </p>
    </div>
  );
}
